import { useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Alert, Box, Button, Slide, Snackbar, useMediaQuery } from "@mui/material";

import { useAuth } from "../context/AuthContext";
import { cashService } from "../services";
import AppBarComponent from "../Components/layout/AppBar";
import SideBarComponent from "../Components/layout/SlideBar";
import CustomTabs from "../Components/layout/CustomTabs";
import { canAccessPermissionEntry } from "../config/permissionUtils";
import {
  privateRoutes,
  publicRoutes,
  sidebarAdminSections,
  sidebarBusinessSections,
} from "./routes.config";
import AuthLoading from "../Components/layout/Skeleton/AuthLoading";

function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

function RequireAuth() {
  const { loadingAuth, isAuthenticated } = useAuth();

  if (loadingAuth) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function NoAccessMessage() {
  return (
    <Box sx={{ py: 4 }}>
      <Alert severity="warning">Sin permiso o modulo deshabilitado.</Alert>
    </Box>
  );
}

function RequirePerm({
  requiredPerms,
  requiredAnyPerms,
  superuserOnly,
  requiredSuperuser,
  requiresCashierQueue,
  requiresBilling,
  requiresDeferredBilling,
  children,
}) {
  const { effectivePerms, isSuperuser, salesConfig } = useAuth();

  const allowed = canAccessPermissionEntry(
    effectivePerms,
    {
      requiredPerms,
      requiredAnyPerms,
      superuserOnly,
      requiredSuperuser,
      requiresCashierQueue,
      requiresBilling,
      requiresDeferredBilling,
    },
    isSuperuser,
    salesConfig
  );

  if (!allowed) {
    return <NoAccessMessage />;
  }

  return children;
}

function PrivateLayout() {
  const { isAuthenticated, currentBranch } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [cashNotice, setCashNotice] = useState({ open: false, key: "", message: "" });
  const isMobile = useMediaQuery("(max-width:768px)");
  const collapsedW = 72;
  const expandedW = 240;
  const contentMarginLeft = isMobile ? 0 : pinned ? expandedW : collapsedW;

  useEffect(() => {
    if (!isAuthenticated) {
      setPinned(false);
      setExpanded(null);
      setDrawerOpen(false);
      setCashNotice({ open: false, key: "", message: "" });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !currentBranch?.id) return undefined;

    let cancelled = false;
    cashService
      .openSessionNotice({ branch_id: currentBranch.id })
      .then((data) => {
        if (cancelled) return;
        const session = data?.session;
        if (!data?.has_open_session || !session || session.status !== "OPEN") return;
        const noticeKey = `cash-open-notice-dismissed:${currentBranch.id}:${session.id}`;
        if (window.sessionStorage.getItem(noticeKey) === "1") return;

        const openedBy =
          session.opened_by_name ||
          session.opened_by_username ||
          session.user_name ||
          session.cashier_name ||
          "usuario";
        setCashNotice({
          open: true,
          key: noticeKey,
          message: `Sucursal con caja abierta por ${openedBy}.`,
        });
      })
      .catch(() => null);

    return () => {
      cancelled = true;
    };
  }, [currentBranch?.id, isAuthenticated]);

  const closeCashNotice = (_event, reason) => {
    if (reason === "clickaway") return;
    if (cashNotice.key) {
      window.sessionStorage.setItem(cashNotice.key, "1");
    }
    setCashNotice((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        bgcolor: (theme) => theme.palette.background.paper,
        overflowX: "hidden",
      }}
    >
      <SideBarComponent
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        pinned={pinned}
        setPinned={setPinned}
        expanded={expanded}
        setExpanded={setExpanded}
        navRoutes={privateRoutes}
        adminSections={sidebarAdminSections}
        businessSections={sidebarBusinessSections}
      />

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <AppBarComponent setDrawerOpen={setDrawerOpen} />

        <Snackbar
          open={cashNotice.open}
          autoHideDuration={60000}
          onClose={closeCashNotice}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={SlideTransition}
          sx={{ mt: { xs: 7.5, sm: 8.5 }, zIndex: (theme) => theme.zIndex.appBar + 1 }}
        >
          <Alert
            severity="info"
            variant="outlined"
            onClose={closeCashNotice}
            action={
              <Button color="inherit" size="small" onClick={closeCashNotice}>
                Aceptar
              </Button>
            }
            sx={{
              bgcolor: "background.paper",
              color: "text.primary",
              borderColor: "divider",
              boxShadow: "0 18px 45px rgba(15, 23, 42, 0.16)",
              minWidth: { xs: "calc(100vw - 32px)", sm: 360 },
              alignItems: "center",
            }}
          >
            {cashNotice.message}
          </Alert>
        </Snackbar>

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            ml: isMobile ? 0 : `${contentMarginLeft}px`,
            width: isMobile ? "100%" : `calc(100% - ${contentMarginLeft}px)`,
            boxSizing: "border-box",
            transition: "margin-left 180ms cubic-bezier(0.2,0,0,1), width 180ms cubic-bezier(0.2,0,0,1)",
            mt: "64px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
            minHeight: "calc(100vh - 64px)",
            backgroundColor: (theme) => theme.palette.background.paper,
            overflowX: "hidden",
          }}
        >
          <Box sx={{ px: 2, pt: 1, pb: 0, flexShrink: 0 }}>
            <CustomTabs />
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, p: 2, pt: 1 }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function AppRouter() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {publicRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            isAuthenticated && route.redirectIfAuthenticated !== false
              ? <Navigate to="/" replace />
              : route.element
          }
        />
      ))}

      <Route element={<RequireAuth />}>
        <Route element={<PrivateLayout />}>
          {privateRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <RequirePerm
                  requiredPerms={route.requiredPerms}
                  requiredAnyPerms={route.requiredAnyPerms}
                  superuserOnly={route.superuserOnly}
                  requiredSuperuser={route.requiredSuperuser}
                  requiresCashierQueue={route.requiresCashierQueue}
                  requiresBilling={route.requiresBilling}
                  requiresDeferredBilling={route.requiresDeferredBilling}
                >
                  {route.element}
                </RequirePerm>
              }
            />
          ))}
          <Route path="*" element={<div>404</div>} />
        </Route>
      </Route>

      {!isAuthenticated && <Route path="*" element={<Navigate to="/login" replace />} />}
    </Routes>
  );
}

export default AppRouter;
